import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import equal from 'fast-deep-equal/es6/react';
import { IN_LAP, IN_SEAT } from '../constants/dropdownConstants';
import { useConfigContext } from './configContext';

export const roomDefaults = {
  adults: 2,
  children: [],
  infants: [],
  pets: 0,
};

const normalizeRoom = (room) => ({
  ...roomDefaults,
  ...room,
  pets: room.pets ?? roomDefaults.pets,
});

export const RoomsContext = createContext({});

export const RoomsContextProvider = ({
  addRoom: addRoomProp,
  children,
  removeRoom: removeRoomProp,
  updateAdults: updateAdultsProp,
  updateChildAge: updateChildAgeProp,
  updateChildCount: updateChildCountProp,
  updateInfantCount: updateInfantCountProp,
  updateInfantInLap: updateInfantInLapProp,
  updatePets: updatePetsProp,
}) => {
  const {
    defaultChildAge,
    defaultValue,
    hasRooms,
    onChange,
    onToggleVisibility,
    value,
  } = useConfigContext();

  const roomsFromParentComponent = useMemo(() => {
    if (value && !Array.isArray(value)) {
      return [normalizeRoom(value)];
    }

    if (value && Array.isArray(value)) {
      return value.map((room) => normalizeRoom(room));
    }

    return [roomDefaults];
  }, [value]);

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [rooms, setRooms] = useState(roomsFromParentComponent);

  const propValueEqualGeneratedDefaultValue = equal(
    roomsFromParentComponent, [roomDefaults]
  );
  const propValueEqualInternalStateValue = equal(rooms, roomsFromParentComponent);

  const addRoom = useCallback(() => {
    setRooms([...rooms, roomDefaults]);
  }, [rooms]);

  const removeRoom = useCallback((removedRoomIndex) => {
    setRooms(rooms.filter((_, roomIndex) => roomIndex !== removedRoomIndex));
  }, [rooms]);

  const setRoomsToDefaultValues = useCallback(() => {
    setRooms(Array.isArray(defaultValue)
      ? defaultValue.map((room) => normalizeRoom(room))
      : defaultValue
    );
  }, [defaultValue]);

  const updateChildrenAndInfantsValues = useCallback((roomIndex, arrayIndex, selectedValue) => {
    const type = selectedValue === IN_LAP || selectedValue === IN_SEAT
      ? 'infants'
      : 'children';
    const updatedRooms = rooms.map((room, index) => {
      let newRoom = room;
      if (index === roomIndex) {
        const values = room[type];
        const newValues = [...values];
        newValues[arrayIndex] = selectedValue;
        newRoom = {
          ...room,
          [type]: newValues,
        };
      }
      return newRoom;
    });
    setRooms(updatedRooms);
  }, [rooms]);

  const updateChildrenAndInfantsCount = useCallback((roomIndex, count, type) => {
    const defaultValueChild = type === 'children' ? defaultChildAge : IN_LAP;

    const updatedRooms = rooms.map((room, index) => {
      if (index === roomIndex) {
        const values = room[type];
        let newValues = [...values];
        while (newValues.length < count) {
          newValues = [...newValues, defaultValueChild];
        }
        return {
          ...room,
          [type]: newValues.slice(0, count),
        };
      }

      return room;
    });

    setRooms(updatedRooms);
  }, [defaultChildAge, rooms]);

  const updateChildCount = useCallback((roomIndex, childCount) => {
    updateChildrenAndInfantsCount(roomIndex, childCount, 'children');
  }, [updateChildrenAndInfantsCount]);

  const updateChildAge = useCallback((roomIndex, arrayIndex, childAge) => {
    updateChildrenAndInfantsValues(roomIndex, arrayIndex, childAge);
  }, [updateChildrenAndInfantsValues]);

  const updateInfantCount = useCallback((roomIndex, infantCount) => {
    updateChildrenAndInfantsCount(roomIndex, infantCount, 'infants');
  }, [updateChildrenAndInfantsCount]);

  const updateInfantInLap = useCallback((roomIndex, arrayIndex, isOnLap) => {
    updateChildrenAndInfantsValues(roomIndex, arrayIndex, isOnLap);
  }, [updateChildrenAndInfantsValues]);

  const updateAdults = useCallback((roomIndex, adultCount) => {
    const updatedRooms = rooms.map(
      (room, index) => {
        if (index === roomIndex) {
          return {
            ...room,
            adults: adultCount,
          };
        }
        return room;
      });

    setRooms(updatedRooms);
  }, [rooms]);

  const updatePets = useCallback((roomIndex, petCount) => {
    const updatedRooms = rooms.map((room, index) => {
      if (index === roomIndex) {
        return {
          ...room,
          pets: petCount,
        };
      }
      return room;
    });

    setRooms(updatedRooms);
  }, [rooms]);

  // Fixes feeding new or updated value other than the generated default value on initial render,
  // specifically when persisting its value in local storage where we need to feed it back
  useEffect(() => {
    if (
      !propValueEqualGeneratedDefaultValue
      && !propValueEqualInternalStateValue
      && !tooltipVisible
    ) {
      setRooms(roomsFromParentComponent);
    }
  }, [roomsFromParentComponent, tooltipVisible]);

  useEffect(() => {
    onToggleVisibility({ tooltipVisible });
  }, [onToggleVisibility, tooltipVisible]);

  useEffect(() => {
    if (!hasRooms && rooms && Array.isArray(rooms) && rooms.length > 1) {
      // Save only the first room
      setRooms(rooms.slice(0, 1));
    }
  }, [hasRooms, rooms]);

  useEffect(() => {
    // Update parent after updating the state
    onChange(rooms);
  }, [rooms]);

  const memoized = useMemo(() => ({
    addRoom: addRoomProp || addRoom,
    removeRoom: removeRoomProp || removeRoom,
    rooms,
    setRoomsToDefaultValues,
    setTooltipVisible,
    tooltipVisible,
    updateAdults: updateAdultsProp || updateAdults,
    updateChildAge: updateChildAgeProp || updateChildAge,
    updateChildCount: updateChildCountProp || updateChildCount,
    updateInfantCount: updateInfantCountProp || updateInfantCount,
    updateInfantInLap: updateInfantInLapProp || updateInfantInLap,
    updatePets: updatePetsProp || updatePets,
  }), [
    addRoom,
    addRoomProp,
    removeRoom,
    removeRoomProp,
    rooms,
    setRoomsToDefaultValues,
    tooltipVisible,
    updateAdults,
    updateAdultsProp,
    updateChildAge,
    updateChildAgeProp,
    updateChildCount,
    updateChildCountProp,
    updateInfantCount,
    updateInfantCountProp,
    updateInfantInLap,
    updateInfantInLapProp,
    updatePets,
    updatePetsProp,
  ]);

  return (
    <RoomsContext.Provider value={memoized}>
      {children}
    </RoomsContext.Provider>
  );
};

RoomsContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  addRoom: PropTypes.func,
  removeRoom: PropTypes.func,
  updateAdults: PropTypes.func,
  updateChildCount: PropTypes.func,
  updateChildAge: PropTypes.func,
  updateInfantCount: PropTypes.func,
  updateInfantInLap: PropTypes.func,
  updatePets: PropTypes.func,
};

export const useRoomsContext = () => {
  const context = useContext(RoomsContext);

  if (!context || Object.keys(context).length === 0) {
    throw new Error('useRoomsContext must be used within RoomsContextProvider.');
  }

  return context;
};
